import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Select } from 'antd';
import styles from '@pages/api-portal/styles.module.scss';
import { API_HTTP_METHODS, API_LIFECYCLE_STATUSES } from '@pages/api-portal/constants';
import { resolveGroupIdForEndpoint } from '@pages/api-portal/utils';
import { ApiEndpoint, ApiGroup, ApiMethod, ApiLifecycleStatus } from '@pages/api-portal/types';

export interface EndpointFormData {
    endpointKey: string;
    name: string;
    method: ApiMethod;
    path: string;
    description: string;
    group: string;
    version: string;
    status: ApiLifecycleStatus;
    tags: string;
}

const DEFAULT_VALUES: EndpointFormData = {
    endpointKey: '',
    name: '',
    method: 'GET',
    path: '',
    description: '',
    group: '',
    version: 'v1',
    status: 'stable',
    tags: '',
};

interface EndpointFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: EndpointFormData) => Promise<void>;
    groups: ApiGroup[];
    endpoint?: ApiEndpoint | null;
}

/*
 * Shared create/edit dialog for API endpoint metadata.
 */
export function EndpointFormDialog({ open, onClose, onSubmit, groups, endpoint }: EndpointFormDialogProps) {
    const isEdit = !!endpoint;
    const [form, setForm] = useState<EndpointFormData>(DEFAULT_VALUES);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
        if (endpoint) {
            setForm({
                endpointKey: endpoint.endpointKey ?? '',
                name: endpoint.name,
                method: endpoint.method,
                path: endpoint.path,
                description: endpoint.description,
                group: resolveGroupIdForEndpoint(endpoint.group, groups),
                version: endpoint.version,
                status: endpoint.status ?? 'stable',
                tags: endpoint.tags.join(', '),
            });
        } else {
            setForm(DEFAULT_VALUES);
        }
    }, [open, endpoint, groups]);

    const isValid = /^[a-z0-9-]+$/.test(form.endpointKey.trim()) && form.name.trim() && form.path.trim() && form.group;

    const handleOk = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await onSubmit(form);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save endpoint.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            title={isEdit ? 'Edit API Endpoint' : 'Create API Endpoint'}
            visible={open}
            onCancel={onClose}
            onOk={handleOk}
            okButtonProps={{ disabled: !isValid, loading: isSubmitting }}
            okText={isEdit ? 'Save Changes' : 'Create'}
            width={640}
            wrapClassName="apimModal"
        >
            {error && <Alert style={{ marginBottom: 12 }} type="error" message={error} showIcon />}

            <div style={{ marginBottom: 12 }}>
                <label className={styles.fieldLabel}>
                    Endpoint Key <span className={styles.requiredMark}>*</span>
                </label>
                <Input
                    placeholder="e.g., instance-delete-instance-property"
                    value={form.endpointKey}
                    onChange={(e) => setForm({ ...form, endpointKey: e.target.value })}
                />
                <p className={styles.fieldHint}>Lowercase letters, numbers, and hyphens only.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                    <label className={styles.fieldLabel}>
                        Name <span className={styles.requiredMark}>*</span>
                    </label>
                    <Input placeholder="e.g., User Login" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                    <label className={styles.fieldLabel}>Method</label>
                    <Select style={{ width: '100%' }} value={form.method} onChange={(method: ApiMethod) => setForm({ ...form, method })}>
                        {API_HTTP_METHODS.map((m) => (
                            <Select.Option key={m} value={m}>
                                {m}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <div style={{ marginBottom: 12 }}>
                <label className={styles.fieldLabel}>
                    Path <span className={styles.requiredMark}>*</span>
                </label>
                <Input placeholder="/api/v1/auth/login" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label className={styles.fieldLabel}>Description</label>
                <Input.TextArea
                    rows={3}
                    placeholder="Describe what this endpoint does..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                    <label className={styles.fieldLabel}>
                        Group <span className={styles.requiredMark}>*</span>
                    </label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select group"
                        value={form.group || undefined}
                        onChange={(group: string) => setForm({ ...form, group })}
                    >
                        {groups.map((g) => (
                            <Select.Option key={String(g.id)} value={String(g.id)}>
                                {g.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <label className={styles.fieldLabel}>Version</label>
                    <Input placeholder="v1" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
                </div>
                <div>
                    <label className={styles.fieldLabel}>Status</label>
                    <Select
                        style={{ width: '100%' }}
                        value={form.status}
                        onChange={(status: ApiLifecycleStatus) => setForm({ ...form, status })}
                    >
                        {API_LIFECYCLE_STATUSES.map((s) => (
                            <Select.Option key={s} value={s}>
                                {s}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <div>
                <label className={styles.fieldLabel}>Tags (comma-separated)</label>
                <Input
                    placeholder="authentication, token, login"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                />
            </div>
        </Modal>
    );
}
